import { useState } from "react";
import axios from "axios";
import clsx from "clsx";
import { Link } from "@inertiajs/react";
import { PlayIcon, ClipboardIcon, ClipboardDocumentCheckIcon, CloudArrowDownIcon } from "@heroicons/react/20/solid";
import { router } from '@inertiajs/react';
import { TableSearchInput } from '../Components/table.jsx';
// import { processZipFile } from '../Utils/dicom.js';

export default function ModelsPage({ models, favoriteIds, requestedIds }) {

    const [searchQuery, setSearchQuery] = useState("");
    const [modalityFilter, setModalityFilter] = useState([]);
    const [typeFilter, setTypeFilter] = useState([]);

    const availableModalities = [...new Set(models.map(model => model.modalities).flat())];
    const availableTypes = [...new Set(models.map(model => model.type).flat())];

    const filteredModels = models.filter(model =>
        (modalityFilter.length === 0 || model.modalities.some(modality => modalityFilter.includes(modality))) &&
        (typeFilter.length === 0 || model.type.some(type => typeFilter.includes(type))) &&
        (
            searchQuery.length <= 3 || 
            model.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
            model.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
            model.type.some(type => type.toLowerCase().includes(searchQuery.toLowerCase()))
        )
    );

    return (
        <div className="px-8 flow-root w-full">

            {/* Header for the confirmation dialog */}
            <div className="sticky top-0 z-10 flex bg-gray-50 py-5 px-2 -mx-2 items-center mb-4 justify-between">
                <div className="flex flex-wrap items-center gap-4">
                    <TableSearchInput
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                    />
                    { availableModalities.length > 0 && availableModalities.map(modality => (
                        <FilterButton
                            key={modality}
                            label={modality.toUpperCase()}
                            isActive={modalityFilter.includes(modality)}
                            onClick={() => {
                                setModalityFilter(prev =>
                                    prev.includes(modality) ? prev.filter(m => m !== modality) : [...prev, modality]
                                );
                            }}
                        />
                    ))} 
                    { availableTypes.length > 0 && availableTypes.map(type => (
                        <FilterButton
                            key={type}
                            label={type.charAt(0).toUpperCase() + type.slice(1)}
                            isActive={typeFilter.includes(type)}
                            onClick={() => {
                                setTypeFilter(prev =>
                                    prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
                                );
                            }}
                        />
                    ))}
                </div>
                <div className="flex gap-4 ml-auto">
                    
                </div>
            </div>
            
            {/* Model details, title, description, modality */}
            <ModelsGrid 
                models={filteredModels} 
                favoriteIds={favoriteIds} 
                requestedIds={requestedIds}
            />
        </div>
    ); 
}

function FilterButton({ label, isActive, onClick }) {
    return (
        <button
            className={clsx(
                "px-4 py-2 rounded-md text-sm font-medium",
                isActive ? "bg-gray-400 text-white" : "bg-gray-200 text-gray-800 hover:bg-gray-300"
            )}
            onClick={onClick}
        >
            {label}
        </button>
    );
}

function ModelsGrid({ models, favoriteIds, requestedIds }) {
    return (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {models.map((model) => {
                const isFavorite = favoriteIds.includes(model.id);
                const isRequested = requestedIds.includes(model.id);
                return <ModelTile 
                            key={model.id} 
                            model={model} 
                            isFavorite={isFavorite} 
                            isRequested={isRequested}
                        />;
            })}
        </div>
    );
}

function ModelTile({ model, isFavorite, isRequested }) {
    const [_isFavorite, setIsFavorite] = useState(isFavorite);
    const [loading, setLoading] = useState(false);

    const modelTypeLabel = model.type.length ? model.type[0].charAt(0).toUpperCase() + model.type[0].slice(1) : "N/A";

    const handleFavoriteToggle = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`/models/${model.id}/favorite`);
            console.log(response.data);
            setIsFavorite(response.data.isFavorite);
        } catch (error) {
            // handle error (optional)
        } finally {
            setLoading(false);
        }
    };

    return (
        <div 
            className={clsx(
                'flex flex-col overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6 relative', 
                model.is_available ? 'hover:shadow-lg transition-shadow group cursor-pointer' : 'cursor-default'
            )}  
            onClick={() => {
                if (model.is_available) {
                    console.log('Model clicked, url:', `/dicomstore/${model.id}`);
                    router.visit(`/dicomstore/${model.id}`);
                }
            }}
        >   
            {/* Favorite icon in upper right */}
            <button
                className="absolute top-3 right-3 text-xl focus:outline-none"
                title={_isFavorite ? "Remove from favorites" : "Add to favorites"}
                onClick={e => {
                    e.stopPropagation(); 
                    handleFavoriteToggle();
                }}
                disabled={loading}
            >
                {loading ? (
                    <svg aria-hidden="true" className="w-4 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-gray-800" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                    </svg>
                ) : _isFavorite ? (
                    <span className="animate-spin text-red-400">&#9733;</span> // filled star
                ) : (
                    <span className="text-gray-400">&#9734;</span> // empty star
                )}
            </button>
            <h2 className="text-lg font-bold text-gray-800">{model.label}</h2>
            <p className="text-sm text-gray-600 flex-grow">{model.description}</p>
            <div className="mt-4 w-full flex items-start flex-wrap gap-2">
                {/* <span className="inline-block rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-800">
                    {model.type}
                </span> */}
              
                {/* <span className="inline-block rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-800 cursor-default">
                    {model.use_gpu ? 'GPU' : 'CPU'}
                </span> */}
                <span className="inline-block flex-none rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-600 cursor-default whitespace-nowrap">
                    {modelTypeLabel}
                </span>
                <span className="inline-block rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-600 cursor-default whitespace-nowrap">
                    {model.modalities.map(m => m.toUpperCase()).join(', ')}
                </span>
                <span className="inline-block rounded-full bg-purple-100 px-3 py-1 text-sm font-semibold text-purple-800 cursor-default whitespace-nowrap">
                    {model.credits} credits
                </span>
                {/* <span className="inline-block rounded-full bg-yellow-100 px-3 py-1 text-sm font-semibold text-yellow-800 cursor-default">
                    {model.is_available ? 'Available' : 'Unavailable'}
                </span> */}

                <div className="flex-grow"></div>

                <button
                    title="Download Sample Data"
                    className="flex h-7 items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold hover:bg-gray-200 transition-all duration text-blue-500 whitespace-nowrap"
                    onClick={e => {
                        e.stopPropagation();
                        axios.get(`/models/${model.id}/sample-data-url`)
                            .then(response => {
                                const sampleDataUrl = response.data.testUrl;
                                console.log('Sample data URL:', sampleDataUrl);
                                if (sampleDataUrl) {


                                    // axios.get(sampleDataUrl, { responseType: 'blob' })
                                    //     .then(res => {
                                    //         const blob = new Blob([res.data], { type: 'application/zip' });
                                    //         processZipFile(blob);
                                    //     })
                                    //     .catch(err => {
                                    //         console.error('Error downloading sample data:', err);
                                    //     });


                                    // Trigger download
                                    const link = document.createElement('a');
                                    link.href = sampleDataUrl;
                                    link.download = `sample_data_${model.id}.zip`;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                }
                            });
                    }}
                >
                    <CloudArrowDownIcon className="inline-block h-4 w-4" />
                </button>

                { model.is_available ?
                    <span className="flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-800 group-hover:bg-green-500 group-hover:text-white transition-all duration text-green-600 whitespace-nowrap">
                        <PlayIcon className="inline-block h-4 w-4 mr-1" />
                        Run Model
                    </span>
                :
                    <Link href={`/models/${model.id}/request`} className="flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold hover:bg-gray-200 transition-all duration text-blue-500 whitespace-nowrap">
                        { isRequested ? 
                            <ClipboardDocumentCheckIcon className="inline-block h-4 w-4 mr-1" />
                        :
                            <ClipboardIcon className="inline-block h-4 w-4 mr-1" />
                        }
                        { isRequested ? 'Requested' : 'Request Model' }
                    </Link>
                }
            </div>
        </div>
    );
}
